// Shared health-state constants mirrored from the DMS backend health engine.
// State -> color mapping (must match backend constants/healthStates.js):
//   HEALTHY -> green; WARNING, IMAGE_STALE -> yellow;
//   OFFLINE, NEVER_REPORTED, NEEDS_REIMAGE -> red;
//   NOT_DEPLOYED, OFF_HOURS -> grey (excluded from fleet counts entirely).

export const HEALTH_STATES = {
  HEALTHY: "HEALTHY",
  WARNING: "WARNING",
  IMAGE_STALE: "IMAGE_STALE",
  OFFLINE: "OFFLINE",
  NEVER_REPORTED: "NEVER_REPORTED",
  NEEDS_REIMAGE: "NEEDS_REIMAGE",
  NOT_DEPLOYED: "NOT_DEPLOYED",
  OFF_HOURS: "OFF_HOURS",
};

// States that mean "this device is not in service", not "this device is broken".
// They are reported in their own tiles and never counted as fleet problems.
export const EXCLUDED_STATES = [
  HEALTH_STATES.NOT_DEPLOYED,
  HEALTH_STATES.OFF_HOURS,
];

// Human-readable label per state.
export const HEALTH_STATE_LABEL = {
  [HEALTH_STATES.HEALTHY]: "Healthy",
  [HEALTH_STATES.WARNING]: "Warning",
  [HEALTH_STATES.IMAGE_STALE]: "Image Stale",
  [HEALTH_STATES.OFFLINE]: "Offline",
  [HEALTH_STATES.NEVER_REPORTED]: "Never Reported",
  [HEALTH_STATES.NEEDS_REIMAGE]: "Needs Re-image",
  [HEALTH_STATES.NOT_DEPLOYED]: "Not Deployed",
  [HEALTH_STATES.OFF_HOURS]: "Off Hours",
};

// antd Tag color per state (semantic antd presets).
export const HEALTH_STATE_TAG_COLOR = {
  [HEALTH_STATES.HEALTHY]: "green",
  [HEALTH_STATES.WARNING]: "gold",
  [HEALTH_STATES.IMAGE_STALE]: "gold",
  [HEALTH_STATES.OFFLINE]: "red",
  [HEALTH_STATES.NEVER_REPORTED]: "red",
  [HEALTH_STATES.NEEDS_REIMAGE]: "volcano",
  [HEALTH_STATES.NOT_DEPLOYED]: "default",
  [HEALTH_STATES.OFF_HOURS]: "default",
};

// The traffic-light "color" bucket the backend assigns to each state.
export const HEALTH_STATE_COLOR = {
  [HEALTH_STATES.HEALTHY]: "green",
  [HEALTH_STATES.WARNING]: "yellow",
  [HEALTH_STATES.IMAGE_STALE]: "yellow",
  [HEALTH_STATES.OFFLINE]: "red",
  [HEALTH_STATES.NEVER_REPORTED]: "red",
  [HEALTH_STATES.NEEDS_REIMAGE]: "red",
  [HEALTH_STATES.NOT_DEPLOYED]: "grey",
  [HEALTH_STATES.OFF_HOURS]: "grey",
};

// Hex for the small status dot (green / yellow / red / grey traffic light).
export const HEALTH_DOT_COLOR = {
  green: "#52c41a",
  yellow: "#faad14",
  red: "#ff4d4f",
  grey: "#bfbfbf",
};

// Rank used to sort so the worst (red) surfaces first and the out-of-service rows
// sink to the bottom — they are not work.
export const HEALTH_COLOR_RANK = {
  red: 0,
  yellow: 1,
  green: 2,
  grey: 3,
};

// Options for the state filter dropdown.
export const HEALTH_STATE_OPTIONS = [
  { value: HEALTH_STATES.HEALTHY, label: HEALTH_STATE_LABEL.HEALTHY },
  { value: HEALTH_STATES.WARNING, label: HEALTH_STATE_LABEL.WARNING },
  { value: HEALTH_STATES.IMAGE_STALE, label: HEALTH_STATE_LABEL.IMAGE_STALE },
  { value: HEALTH_STATES.OFFLINE, label: HEALTH_STATE_LABEL.OFFLINE },
  {
    value: HEALTH_STATES.NEVER_REPORTED,
    label: HEALTH_STATE_LABEL.NEVER_REPORTED,
  },
  {
    value: HEALTH_STATES.NEEDS_REIMAGE,
    label: HEALTH_STATE_LABEL.NEEDS_REIMAGE,
  },
  { value: HEALTH_STATES.NOT_DEPLOYED, label: HEALTH_STATE_LABEL.NOT_DEPLOYED },
  { value: HEALTH_STATES.OFF_HOURS, label: HEALTH_STATE_LABEL.OFF_HOURS },
];

// The states that are actual fleet problems — what the "By State" panel shows.
export const PROBLEM_STATE_OPTIONS = HEALTH_STATE_OPTIONS.filter(
  (o) => !EXCLUDED_STATES.includes(o.value)
);

// Options for the color filter dropdown.
export const HEALTH_COLOR_OPTIONS = [
  { value: "green", label: "Green" },
  { value: "yellow", label: "Yellow" },
  { value: "red", label: "Red" },
  { value: "grey", label: "Grey (not in service)" },
];

// --- Device lifecycle (mirrors backend constants/deviceLifecycle.js) ----------
// Only DEPLOYED devices are counted in fleet health. Everything else is reported
// separately, so a box on the bench can never turn the board red.

export const LIFECYCLE = {
  QC: "QC",
  DEPLOYED: "DEPLOYED",
  MAINTENANCE: "MAINTENANCE",
  RETIRED: "RETIRED",
};

export const LIFECYCLE_LABEL = {
  [LIFECYCLE.QC]: "QC / Bench",
  [LIFECYCLE.DEPLOYED]: "Deployed",
  [LIFECYCLE.MAINTENANCE]: "Maintenance",
  [LIFECYCLE.RETIRED]: "Retired",
};

export const LIFECYCLE_TAG_COLOR = {
  [LIFECYCLE.QC]: "default",
  [LIFECYCLE.DEPLOYED]: "blue",
  [LIFECYCLE.MAINTENANCE]: "orange",
  [LIFECYCLE.RETIRED]: "default",
};

export const LIFECYCLE_OPTIONS = [
  { value: LIFECYCLE.DEPLOYED, label: LIFECYCLE_LABEL.DEPLOYED },
  { value: LIFECYCLE.QC, label: LIFECYCLE_LABEL.QC },
  { value: LIFECYCLE.MAINTENANCE, label: LIFECYCLE_LABEL.MAINTENANCE },
];

// alert severity -> antd Tag color. Backend severities are the traffic-light
// colors (green/yellow/red) plus "info" (see Alert model).
export const ALERT_SEVERITY_TAG_COLOR = {
  red: "red",
  yellow: "gold",
  green: "green",
  info: "blue",
};

// Build the static image URL for a stored picture `path` (which contains
// "/uploads/..."). The web host is REACT_APP_BASE_URL minus a trailing "/api".
export const pictureUrlFromPath = (path, baseUrl = process.env.REACT_APP_BASE_URL) => {
  if (!path || typeof path !== "string") return null;
  const idx = path.indexOf("/uploads");
  const rel = idx >= 0 ? path.slice(idx) : `/${path.replace(/^\/+/, "")}`;
  const host = (baseUrl || "").replace(/\/api\/?$/, "").replace(/\/+$/, "");
  return `${host}${rel}`;
};

// alert status -> antd Tag color.
export const ALERT_STATUS_TAG_COLOR = {
  open: "red",
  resolved: "green",
};

// Render a timestamp as a short relative-ish string (e.g. "5m ago").
// Falls back to a locale string for anything older than a day.
export const relativeTime = (value) => {
  if (!value) return "Never";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "—";
  const diffMs = Date.now() - then;
  if (diffMs < 0) return new Date(value).toLocaleString();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(value).toLocaleString();
};

// hardwareAdmin and above may resolve alerts / edit alert rules.
// (Kept local to health features; mirrors backend authorization.)
export const HARDWARE_ADMIN_RANK = 3;
